// src/services/api/promo/promoApi.js
import { ENDPOINTS, METHODS } from "@/constants/apiConstants";
import { apiSlice } from "../baseApi";

const promoBaseEndpoint = ENDPOINTS.PROMOS; // e.g. "/promos"
const rankBaseEndpoint = ENDPOINTS.RANKS; // e.g. "/ranks"
const prompTypeBaseEndpoint = ENDPOINTS.PROMOTYPE;

export const promoApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ["Promo", "Rank", "PromoType"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      /* -------------------------------------------------
         PROMO ENDPOINTS
         ------------------------------------------------- */
      getPromos: builder.query({
        query: ({ page = 1, per_page = 10, search = "" } = {}) => ({
          url: promoBaseEndpoint,
          params: {
            page,
            per_page,
            ...(search && { search }),
          },
        }),
        transformResponse: (response) => {
          return {
            data: response.data, // or response.items, depending on your API
            meta: response.meta,
          };
        },
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: "Promo", id })),
                { type: "Promo", id: "LIST" },
              ]
            : [{ type: "Promo", id: "LIST" }],
      }),

      getPromo: builder.query({
        query: (id) => `${promoBaseEndpoint}/${id}`,
        transformResponse: (response) => response?.data || response,
        providesTags: (result, error, id) => [{ type: "Promo", id }],
      }),

      addPromo: builder.mutation({
        query: (requestData) => ({
          url: promoBaseEndpoint,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: [{ type: "Promo", id: "LIST" }],
      }),

      updatePromo: builder.mutation({
        query: ({ id, ...requestData }) => ({
          url: `${promoBaseEndpoint}/${id}`,
          method: METHODS.PUT,
          body: requestData,
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: "Promo", id },
          { type: "Promo", id: "LIST" },
        ],
      }),

      deletePromo: builder.mutation({
        query: (id) => ({
          url: `${promoBaseEndpoint}/${id}`,
          method: METHODS.DELETE,
        }),
        invalidatesTags: (result, error, id) => [
          { type: "Promo", id },
          { type: "Promo", id: "LIST" },
        ],
      }),

      /* -------------------------------------------------
        PROMOTYPE ENDPOINTS
         ------------------------------------------------- */

      getPromoType: builder.query({
        query: () => `${prompTypeBaseEndpoint}`,
        transformResponse: (response) => response?.data || response,
        providesTags: (result, error, id) => [{ type: "PromoType", id }],
      }),

      addPromoType: builder.mutation({
        query: (requestData) => ({
          url: prompTypeBaseEndpoint,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: [{ type: "PromoType", id: "LIST" }],
      }),

      updatePromoType: builder.mutation({
        query: ({ id, ...requestData }) => ({
          url: `${prompTypeBaseEndpoint}/${id}`,
          method: METHODS.PUT,
          body: requestData,
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: "PromoType", id },
          { type: "PromoType", id: "LIST" },
        ],
      }),
      deletepromoType: builder.mutation({
        query: (id) => ({
          url: `${rankBaseEndpoint}/${id}`,
          method: METHODS.DELETE,
        }),
        invalidatesTags: (result, error, id) => [
          { type: "PromoType", id },
          { type: "PromoType", id: "LIST" },
        ],
      }),

      /* -------------------------------------------------
         RANK ENDPOINTS
         ------------------------------------------------- */
      getRanks: builder.query({
        query: ({ page = 1, per_page = 10, search = "" } = {}) => ({
          url: rankBaseEndpoint,
          params: {
            page,
            per_page,
            ...(search && { search }),
          },
        }),
        transformResponse: (response) => response?.data || response,
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Rank", id })),
                { type: "Rank", id: "LIST" },
              ]
            : [{ type: "Rank", id: "LIST" }],
      }),

      addRank: builder.mutation({
        query: (requestData) => ({
          url: rankBaseEndpoint,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: [{ type: "Rank", id: "LIST" }],
      }),

      updateRank: builder.mutation({
        query: ({ id, ...requestData }) => ({
          url: `${rankBaseEndpoint}/${id}`,
          method: METHODS.PUT,
          body: requestData,
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: "Rank", id },
          { type: "Rank", id: "LIST" },
        ],
      }),

      deleteRank: builder.mutation({
        query: (id) => ({
          url: `${rankBaseEndpoint}/${id}`,
          method: METHODS.DELETE,
        }),
        invalidatesTags: (result, error, id) => [
          { type: "Rank", id },
          { type: "Rank", id: "LIST" },
        ],
      }),
    }),
  });

// Export all hooks
export const {
  // Promo hooks
  useGetPromosQuery,
  useGetPromoQuery,
  useAddPromoMutation,
  useUpdatePromoMutation,
  useDeletePromoMutation,

  // Rank hooks
  useGetRanksQuery,
  useAddRankMutation,
  useUpdateRankMutation,
  useDeleteRankMutation,

  //   PromoType hooks
  useAddPromoTypeMutation,
  useGetPromoTypeQuery,
  useUpdatePromoTypeMutation,
} = promoApi;
